import { forwardRef, useId } from "react";
import type { ChangeEventHandler, FocusEventHandler, ReactNode } from "react";

type BaseFieldProps = {
  label: ReactNode;
  name: string;
  helperText?: string;
  error?: string;
  required?: boolean;
};

export function FormField({
  label,
  helperText,
  error,
  required,
  group,
  children,
}: BaseFieldProps & {
  children: ReactNode;
  /**
   * The child BRINGS ITS OWN `<label>` — render a labelled group instead of
   * wrapping everything in a second one.
   *
   * This exists because of a defect, so here is the defect. `FormField` is a
   * `<label>` with no `htmlFor`: it names its control by containing it, which
   * is correct and is why it has no `id` to point at. `Checkbox` is ALSO a
   * `<label>` whenever it is given a `label` or `description` prop, for the
   * same good reason. Put one inside the other — which the restaurant's
   * general-settings screen does thirteen times, on the page holding VAT, tax
   * registration, service charge and the write-off VAT treatment — and the
   * result is a nested `<label>`. That is invalid HTML, and the consequence is
   * not cosmetic: the accessible name of the inner control is computed from
   * two competing labels and the answer is not deterministic across engines,
   * while a click on the OUTER label's text can toggle a checkbox the reader
   * never associated with it.
   *
   * With `group`, the wrapper becomes a `<div role="group">` named by the same
   * visible text through `aria-labelledby`. Nothing moves and nothing is
   * restyled; the reader now hears the group's name, then the checkbox's own
   * name, which is what the markup always meant.
   *
   * Opt-in, so every existing caller in seller, admin and buyer is untouched.
   */
  group?: boolean;
}) {
  const labelId = `${useId()}-label`;
  const Wrapper = group ? "div" : "label";
  return (
    <Wrapper
      data-ui="field"
      className="flex flex-col gap-1.5"
      {...(group ? { role: "group", "aria-labelledby": labelId } : {})}
    >
      <span
        data-ui="field-label"
        id={group ? labelId : undefined}
        className="text-[12px] font-medium leading-tight text-[color:var(--label-ink)]"
      >
        {label}
        {required ? (
          <>
            {" "}
            <span
              data-ui="field-required"
              className="text-[color:var(--error-500)]"
            >
              *
            </span>
          </>
        ) : null}
      </span>
      {children}
      {error ? (
        <span
          data-ui="field-error"
          className="text-[11px] font-normal leading-snug text-[color:var(--error-500)]"
          aria-live="polite"
        >
          {error}
        </span>
      ) : helperText ? (
        <span
          data-ui="field-hint"
          className="text-[11px] font-normal leading-snug text-[color:var(--text-muted)]"
        >
          {helperText}
        </span>
      ) : null}
    </Wrapper>
  );
}

/*
 * Control sizing is ONE convention across this library.
 *
 * `Combobox` has always been `h-11 lg:h-[35px]` with `text-[16px]
 * lg:text-[13px]`: a 44px touch target and a 16px font on a phone, collapsing
 * to the desk-bound 35px/13px at `lg`. `TextInput` and `TextArea` were a flat
 * 35px/13px, so a form pairing a picker with a field rendered two different
 * control heights side by side on mobile — visible in the restaurant portal's
 * table modal, where a 35px area picker sat directly above a 44px name field.
 * The library was disagreeing with itself; the apps just showed it.
 *
 * The 16px on mobile is not cosmetic: iOS Safari zooms the page on focus for
 * any input under 16px, which throws the layout out from under the user
 * mid-form.
 */
export const TextInput = forwardRef<
  HTMLInputElement,
  {
    name: string;
    type?: string;
    defaultValue?: string;
    value?: string;
    placeholder?: string;
    required?: boolean;
    hasError?: boolean;
    /** Id of the element describing this field — normally the error message.
     *  `hasError` alone only tells a screen reader that the value is invalid;
     *  this is what lets it read out WHY. */
    describedBy?: string;
    /** Forwarded to the rendered element. These components declare their props
     *  explicitly rather than spreading, so an undeclared `data-testid` type-
     *  checks (TS exempts hyphenated JSX attributes) and is then silently
     *  dropped — which is how a test id can disappear without anything failing. */
    "data-testid"?: string;
    "aria-label"?: string;
    disabled?: boolean;
    readOnly?: boolean;
    onFocus?: FocusEventHandler<HTMLInputElement>;
    min?: number | string;
    max?: number | string;
    step?: number | string;
    maxLength?: number;
    autoComplete?: string;
    autoFocus?: boolean;
    autoCapitalize?: "none" | "on" | "characters" | "words" | "sentences";
    dir?: "ltr" | "rtl" | "auto";
    inputMode?:
      | "none"
      | "text"
      | "tel"
      | "url"
      | "email"
      | "numeric"
      | "decimal"
      | "search";
    enterKeyHint?:
      | "enter"
      | "done"
      | "go"
      | "next"
      | "previous"
      | "search"
      | "send";
    className?: string;
    /** Decoration rendered absolutely on the leading edge (e.g. search icon). */
    prefix?: ReactNode;
    /** Decoration rendered absolutely on the trailing edge (e.g. verified badge, eye toggle). */
    suffix?: ReactNode;
    /** Override the auto-padding when the leading slot is wider than default (32px). */
    prefixSpace?: number;
    /** Override the auto-padding when the trailing slot is wider than default (32px). */
    suffixSpace?: number;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onBlur?: ChangeEventHandler<HTMLInputElement>;
  }
>(function TextInput(
  {
    name,
    type = "text",
    defaultValue,
    value,
    placeholder,
    required,
    hasError,
    describedBy,
    disabled,
    readOnly,
    onFocus,
    min,
    max,
    step,
    maxLength,
    autoComplete,
    autoFocus,
    autoCapitalize,
    dir,
    inputMode,
    enterKeyHint,
    className,
    prefix,
    suffix,
    prefixSpace = 32,
    suffixSpace = 32,
    onChange,
    onBlur,
    "data-testid": testId,
    "aria-label": ariaLabel,
  },
  ref,
) {
  const isControlled = value !== undefined;
  const input = (
    <input
      ref={ref}
      name={name}
      type={type}
      defaultValue={isControlled ? undefined : defaultValue}
      value={value}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      readOnly={readOnly}
      onFocus={onFocus}
      min={min}
      max={max}
      step={step}
      maxLength={maxLength}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      autoCapitalize={autoCapitalize}
      dir={dir}
      inputMode={inputMode}
      enterKeyHint={enterKeyHint}
      aria-invalid={hasError ? "true" : undefined}
      aria-describedby={describedBy}
      data-testid={testId}
      aria-label={ariaLabel}
      data-ui="input"
      onChange={onChange}
      onBlur={onBlur}
      style={{
        paddingInlineStart: prefix ? prefixSpace : undefined,
        paddingInlineEnd: suffix ? suffixSpace : undefined,
      }}
      className={`h-11 w-full rounded-[var(--radius-input,6px)] border-[length:var(--border-control-width,1px)] bg-[color:var(--surface)] px-3 text-[16px] focus:outline-none lg:h-[35px] lg:text-[13px] ${
        hasError
          ? "border-[color:var(--error-500)] focus:border-[color:var(--error-500)]"
          : "border-[color:var(--border-light)] focus:border-[color:var(--focus-ring)]"
      } ${disabled ? "cursor-not-allowed bg-[color:var(--background-muted)] text-[color:var(--text-muted)]" : ""} ${className ?? ""}`}
    />
  );
  if (!prefix && !suffix) return input;
  return (
    <div className="relative">
      {input}
      {prefix ? (
        <span className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3 text-[color:var(--text-muted)]">
          {prefix}
        </span>
      ) : null}
      {suffix ? (
        <span className="absolute inset-y-0 end-0 flex items-center pe-2 text-[color:var(--text-muted)]">
          {suffix}
        </span>
      ) : null}
    </div>
  );
});

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  {
    name: string;
    defaultValue?: string;
    value?: string;
    placeholder?: string;
    required?: boolean;
    hasError?: boolean;
    dir?: "ltr" | "rtl" | "auto";
    rows?: number;
    maxLength?: number;
    /** Read-only for a session that may see the text but not change it. */
    disabled?: boolean;
    onChange?: ChangeEventHandler<HTMLTextAreaElement>;
    onBlur?: ChangeEventHandler<HTMLTextAreaElement>;
  }
>(function TextArea(
  {
    name,
    defaultValue,
    value,
    placeholder,
    required,
    hasError,
    dir,
    rows = 4,
    maxLength,
    disabled,
    onChange,
    onBlur,
  },
  ref,
) {
  const isControlled = value !== undefined;
  return (
    <textarea
      ref={ref}
      data-ui="textarea"
      name={name}
      defaultValue={isControlled ? undefined : defaultValue}
      value={value}
      placeholder={placeholder}
      required={required}
      dir={dir}
      maxLength={maxLength}
      disabled={disabled}
      aria-invalid={hasError ? "true" : undefined}
      onChange={onChange}
      onBlur={onBlur}
      rows={rows}
      className={`min-h-[84px] w-full rounded-[var(--radius-input,6px)] border-[length:var(--border-control-width,1px)] bg-[color:var(--surface)] px-3 py-2.5 text-[16px] focus:outline-none lg:text-[13px] ${
        hasError
          ? "border-[color:var(--error-500)] focus:border-[color:var(--error-500)]"
          : "border-[color:var(--border-light)] focus:border-[color:var(--focus-ring)]"
      }`}
    />
  );
});

export function ToggleInput({
  name,
  defaultChecked,
  checked,
  value,
  disabled,
  onChange,
  onBlur,
  ariaLabel,
}: {
  name: string;
  defaultChecked?: boolean;
  checked?: boolean;
  value?: string;
  disabled?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.ChangeEventHandler<HTMLInputElement>;
  /**
   * Accessible name for the toggle. Required when the toggle is rendered
   * without a visible associated label (e.g. inside a card footer).
   */
  ariaLabel?: string;
}) {
  return (
    <label
      data-ui="switch"
      className="relative inline-flex h-6 w-11 cursor-pointer items-center"
    >
      <input
        name={name}
        type="checkbox"
        defaultChecked={defaultChecked}
        checked={checked}
        value={value}
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
        aria-label={ariaLabel}
        className="peer sr-only"
      />
      <span
        data-ui="toggle"
        className="h-6 w-11 rounded-full bg-[color:var(--border-light)] transition-colors peer-checked:bg-[color:var(--accent-500)] peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[color:var(--accent-500)] peer-disabled:cursor-not-allowed peer-disabled:opacity-60"
      />
      <span
        data-ui="toggle-knob"
        className="pointer-events-none absolute start-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5 peer-checked:bg-[color:var(--on-accent)] rtl:peer-checked:-translate-x-5"
      />
    </label>
  );
}
